import classNames from "classnames";
import React from "react";
import style from "../../scss/export.module.scss";
import { Accordion } from "../Accordion";
import { Button } from "../Button";
import { Heading } from "../Heading";
import { UserIcon } from "../Icons";
import { Input } from "../Input";
import { LanguageSelector } from "../LanguageSelector";
import { Logotype } from "../Logotype";
import { Notification } from "../Notification";
import { TSelectOption } from "../Select";
import { ButtonSkeleton, InputSkeleton, Skeleton, SkeletonGroup } from "../Skeleton";
import "./LoginPage.scss";

type TLoginPage = {
    logo: string;
    username: string;
    password: string;
    onUsernameChange: (username: string) => void;
    onPasswordChange: (password: string) => void;
    hasLoginError?: boolean;
    hasAuthCheckError?: boolean;
    isLoggingIn?: boolean;
    onSubmit: (arg0: React.FormEvent) => void;
    texts?: TLoginPageTexts;
    defaultLanguage?: string;
    languageOptions?: TSelectOption[];
    onChangeLanguage?: (language: string) => void;
    showSkeleton?: boolean;
    active?: boolean;
};

export type TLoginPageTexts = {
    description?: string;
    login?: string;
    loginError?: string;
    authCheckError?: string;
    username?: string;
    usernamePlaceholder?: string;
    password?: string;
    passwordPlaceolder?: string;
    forgottenPasswordTitle?: string;
    forgottenPasswordDescription?: string;
    policyLink?: string[];
    inActive?: string;
};

const defaultTexts: TLoginPageTexts = {
    description: "Signatur och lösenord är de samma som i affärssystemet.",
    login: "Logga in",
    loginError: "Signaturen eller lösenordet är felaktigt. Vänligen försök igen.",
    authCheckError: "Du har blivit utloggad.",
    username: "Signatur",
    usernamePlaceholder: "Din signatur",
    password: "Lösenord",
    passwordPlaceolder: "Ditt lösenord",
    forgottenPasswordTitle: "Glömt lösenord?",
    forgottenPasswordDescription:
        "Vänligen kontakta ansvarig systemadministratör på anläggningen för att få hjälp med att byta lösenord.",
    policyLink: ["Genom att logga in godkänner du vår", "integritetspolicy"],
    inActive: "Denna produkt är inaktiverad. Vänligen kontakta ansvarig systemadministratör.",
};

export const LoginPage = ({
    logo,
    username,
    password,
    onUsernameChange,
    onPasswordChange,
    hasLoginError = false,
    hasAuthCheckError = false,
    isLoggingIn = false,
    onSubmit,
    texts,
    defaultLanguage = "sv",
    languageOptions,
    onChangeLanguage,
    showSkeleton = false,
    active = false,
}: TLoginPage) => {
    const textsToUse = { ...defaultTexts, ...texts };

    return (
        <div className="login-page-component">
            {!!languageOptions?.length && onChangeLanguage && (
                <div className="login-page-component__language-picker">
                    <LanguageSelector
                        defaultLanguage={defaultLanguage}
                        onLanguageChange={onChangeLanguage}
                        languageOptions={languageOptions}
                    />
                </div>
            )}
            <div className="login-page-component__content">
                <img src={logo} alt="logotype" className="login-page-component__admin-logo" />
                {active && (
                    <p
                        className={classNames("login-page-component__login-description-text", {
                            "login-page-component__login-description-text--hidden": showSkeleton,
                        })}
                    >
                        {textsToUse.description}
                    </p>
                )}
                <div className="login-page-component__box">
                    {showSkeleton ? (
                        <SkeletonGroup>
                            <div className="u-d-flex u-flex-column u-align-items-center">
                                <div className="u-mb-6">
                                    <Skeleton height="37px" width="150px" shade="dark" />
                                </div>
                                <div className="u-mb-12 u-w-100">
                                    <InputSkeleton shade="dark" />
                                </div>
                                <div className="u-mb-24 u-w-100">
                                    <InputSkeleton shade="dark" />
                                </div>
                                <ButtonSkeleton shade="dark" width="100%" />
                            </div>
                        </SkeletonGroup>
                    ) : (
                        <>
                            <Heading type="h3" className="login-page-component__login-text">
                                {textsToUse.login}
                            </Heading>
                            {active ? (
                                <>
                                    <Notification
                                        type="error"
                                        show={hasLoginError && !hasAuthCheckError}
                                        className="u-mb-18"
                                    >
                                        <p>{textsToUse.loginError}</p>
                                    </Notification>
                                    <Notification type="warning" show={hasAuthCheckError} className="u-mb-18">
                                        <p>{textsToUse.authCheckError}</p>
                                    </Notification>
                                    <form onSubmit={onSubmit}>
                                        <div className="u-mb-12">
                                            <Input
                                                label={textsToUse.username}
                                                value={username}
                                                placeholder={textsToUse.usernamePlaceholder}
                                                onChange={e => onUsernameChange(e.target.value)}
                                                onDarkBackground
                                                disabled={isLoggingIn}
                                            />
                                        </div>
                                        <div className="u-mb-24">
                                            <Input
                                                label={textsToUse.password}
                                                type="password"
                                                value={password}
                                                placeholder={textsToUse.passwordPlaceolder}
                                                onChange={e => onPasswordChange(e.target.value)}
                                                onDarkBackground
                                                disabled={isLoggingIn}
                                            />
                                        </div>
                                        <Button
                                            type="login"
                                            submit
                                            rightIcon={<UserIcon />}
                                            fullWidth
                                            buttonClassName="login-page-component__login-button"
                                            disabled={!username || !password}
                                            loading={isLoggingIn}
                                        >
                                            {textsToUse.login}
                                        </Button>
                                        <p className="u-color-white u-fs-12 u-mb-0">
                                            {`${textsToUse.policyLink?.[0]} `}
                                            <a
                                                className="u-link-on-dark-bg"
                                                href="https://www.r360.se/integritetspolicy"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {textsToUse.policyLink?.[1]}.
                                            </a>
                                        </p>
                                    </form>
                                </>
                            ) : (
                                <div className="login-page-component__inactive">
                                    <i>{textsToUse.inActive}</i>
                                </div>
                            )}
                        </>
                    )}
                </div>
                {active && (
                    <div
                        className={classNames("login-page-component__forgotten-password", {
                            "login-page-component__forgotten-password--hidden": showSkeleton,
                        })}
                    >
                        <Accordion
                            title={textsToUse.forgottenPasswordTitle ?? ""}
                            stripped
                            btnTextColor={style.whiteColor}
                        >
                            <div style={{ height: "100%" }}>
                                <p className="login-page-component__forgotten-password-text">
                                    {textsToUse.forgottenPasswordDescription}
                                </p>
                            </div>
                        </Accordion>
                    </div>
                )}
            </div>
            <div className="login-page-component__login-footer">
                <Logotype color="white" width={75} />
            </div>
        </div>
    );
};
